import { useState, useRef, useCallback, useEffect } from 'react';
import { io, Socket } from 'socket.io-client';
import {
  DEFAULT_WEBRTC_CONFIG,
  VIDEO_CONSTRAINTS,
  CallState,
  ConnectionState,
  WebRTCError,
  getMediaStream,
  generateSessionId,
  SDPMessage,
  ICECandidateMessage,
  CallMetadata
} from '../utils/webrtc-config';

export interface UseWebRTCOptions {
  socketUrl?: string;
  config?: typeof DEFAULT_WEBRTC_CONFIG;
  autoConnect?: boolean;
  enableVideo?: boolean;
  /** Agent's user ID — included in agent:register and call metadata for caller/receiver tracking. */
  agentId?: string;
  onStateChange?: (state: CallState) => void;
  onConnectionStateChange?: (state: ConnectionState) => void;
  onRemoteStream?: (stream: MediaStream) => void;
  onError?: (error: WebRTCError | string) => void;
}

export interface UseWebRTCReturn {
  // State
  callState: CallState;
  connectionState: ConnectionState;
  localStream: MediaStream | null;
  remoteStream: MediaStream | null;
  isMuted: boolean;
  sessionId: string | null;
  incomingCallData: {
    session_id: string;
    caller_id: string;
    call_type?: 'audio' | 'video';
    conversation_id?: string;
    timestamp?: string;
  } | null;
  error: string | null;
  /** @deprecated Use myCaption / remoteCaption for speaker-attributed captions. */
  liveCaption: string;
  /** Captions of the local user's own speech (speaker_sid == socket.id). */
  myCaption: string;
  /** Captions of the remote party's speech (speaker_sid != socket.id). */
  remoteCaption: string;
  /** True when the remote peer has sent a video upgrade request and is awaiting our answer. */
  videoUpgradeRequested: boolean;

  // Actions
  startCall: (targetAgentId?: string, metadata?: any) => Promise<void>;
  acceptCall: (sessionId: string, callType?: 'audio' | 'video') => Promise<void>;
  declineCall: (sessionId: string) => Promise<void>;
  endCall: () => Promise<void>;
  toggleMute: () => void;
  toggleVideo: () => void;
  startLocalStream: () => Promise<void>;
  stopLocalStream: () => void;
  isVideoEnabled: boolean;
  /** Accept a pending video upgrade request from the remote peer. */
  acceptVideoUpgrade: () => Promise<void>;
  /** Decline a pending video upgrade request from the remote peer. */
  declineVideoUpgrade: () => void;
  /** True while a combined local+remote MediaRecorder is actively recording. */
  isRecording: boolean;
  
  // Connection management
  connect: () => void;
  disconnect: () => void;
  /** The live Socket.IO socket — useful for emitting custom events (e.g. audio chunks). */
  socket: import('socket.io-client').Socket | null;

  // Agent management
  registerAsAgent: () => void;
  unregisterAsAgent: () => void;
  registerAsCustomer: (customerId: string) => void;
}

/**
 * Custom hook for WebRTC peer-to-peer voice calling
 * Handles all WebRTC peer connection logic and signaling
 */
export function useWebRTC(options: UseWebRTCOptions = {}): UseWebRTCReturn {
  const {
    socketUrl = typeof window !== 'undefined'
      ? (window as any).REACT_APP_SOCKET_URL || process.env.REACT_APP_SOCKET_URL || window.location.origin
      : process.env.REACT_APP_SOCKET_URL || 'http://localhost:8001',
    config = DEFAULT_WEBRTC_CONFIG,
    autoConnect = true,
    enableVideo = false,
    agentId,
    onStateChange,
    onConnectionStateChange,
    onRemoteStream,
    onError
  } = options;

  // State management
  const [callState, setCallState] = useState<CallState>(CallState.IDLE);
  const [connectionState, setConnectionState] = useState<ConnectionState>(ConnectionState.NEW);
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
  const [isMuted, setIsMuted] = useState<boolean>(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isVideoEnabled, setIsVideoEnabled] = useState<boolean>(enableVideo);
  const [error, setError] = useState<string | null>(null);
  // Accumulated live-caption text from Whisper (arrives as the call progresses)
  const [liveCaption, setLiveCaption] = useState<string>('');
  const liveCaptionAccRef = useRef<string>('');
  // Speaker-attributed captions — split by whether speaker_sid == our own socket id
  const [myCaption, setMyCaption] = useState<string>('');
  const [remoteCaption, setRemoteCaption] = useState<string>('');
  const myCaptionAccRef = useRef<string>('');
  const remoteCaptionAccRef = useRef<string>('');
  // True when the remote peer requested a video upgrade and is waiting for our answer
  const [videoUpgradeRequested, setVideoUpgradeRequested] = useState<boolean>(false);
  // True while the combined local+remote call recorder is running
  const [isRecording, setIsRecording] = useState<boolean>(false);
  const [incomingCallData, setIncomingCallData] = useState<{
    session_id: string;
    caller_id: string;
    call_type?: 'audio' | 'video';
    conversation_id?: string;
    timestamp?: string;
    // 2026-10-04 (warm transfer): context the AI sends with a transferred call.
    handoff?: Record<string, string>;
  } | null>(null);

  // Refs for persistent data
  const socketRef = useRef<Socket | null>(null);
  const peerConnectionRef = useRef<RTCPeerConnection | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const remoteStreamRef = useRef<MediaStream | null>(null);
  const isAgentRef    = useRef<boolean>(false);
  const customerIdRef = useRef<string | undefined>(undefined);
  const metadataRef = useRef<CallMetadata>({});
  // Keep agentId in a ref so it's readable inside callbacks without dep-array churn
  const agentIdRef = useRef<string | undefined>(agentId);
  useEffect(() => { agentIdRef.current = agentId; }, [agentId]);
  // MediaRecorder used to stream remote audio to the backend for transcription
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const pendingCandidatesRef = useRef<RTCIceCandidateInit[]>([]);
  // ── Call recording refs ───────────────────────────────────────────────────
  const callRecorderRef = useRef<MediaRecorder | null>(null);
  const callRecordingChunksRef = useRef<Blob[]>([]);
  const callAudioCtxRef = useRef<AudioContext | null>(null);
  const recordingSessionRef = useRef<string | null>(null);
  // Keep a ref in sync with sessionId state so socket listeners can read the
  // current value without needing to be in the `connect` dependency array
  // (being in that array caused the socket to be torn down and recreated on
  // every call that set a new session ID).
  const sessionIdRef = useRef<string | null>(null);

  // Event handlers
  const handleStateChange = useCallback((newState: CallState) => {
    setCallState(newState);
    onStateChange?.(newState);
  }, [onStateChange]);

  const handleConnectionStateChange = useCallback((newState: ConnectionState) => {
    setConnectionState(newState);
    onConnectionStateChange?.(newState);
  }, [onConnectionStateChange]);

  const handleError = useCallback((error: WebRTCError | string) => {
    setError(error.toString());
    handleStateChange(CallState.ERROR);
    onError?.(error);
  }, [handleStateChange, onError]);

  // Keep a ref in sync with isVideoEnabled so startLocalStream can read it
  // without needing to be in the startLocalStream dependency array.
  const videoEnabledRef = useRef<boolean>(enableVideo);
  useEffect(() => {
    videoEnabledRef.current = isVideoEnabled;
  }, [isVideoEnabled]);

  // Keep sessionIdRef in sync so socket listeners always read the latest value
  // without requiring sessionId in the connect() dependency array.
  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  // ── Remote audio streaming → backend transcription ───────────────────────
  // When the call is CONNECTED and a remote stream is available, start a
  // MediaRecorder that ships 4-second WebM/Opus chunks to the backend via
  // the `audio_data_chunk` Socket.IO event.  The backend accumulates the
  // chunks and forwards them to Whisper at call-end.
  useEffect(() => {
    // Stop any existing recorder whenever call state or stream changes
    const stopExisting = () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      mediaRecorderRef.current = null;
    };

    if (callState !== CallState.CONNECTED || !remoteStream) {
      stopExisting();
      return;
    }

    // Only record live audio tracks
    const audioTracks = remoteStream.getAudioTracks().filter(t => t.readyState === 'live');
    if (audioTracks.length === 0) return;

    const mimeType =
      ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus']
        .find(t => MediaRecorder.isTypeSupported(t)) ?? '';

    if (!mimeType) {
      console.warn('[useWebRTC] No supported MediaRecorder audio MIME type — transcription skipped');
      return;
    }

    try {
      const audioStream = new MediaStream(audioTracks);
      let active = true;
      // ── FIX 1: Capture session ID at recorder-start time ─────────────────
      // endCall() calls setSessionId(null) which eventually makes
      // sessionIdRef.current null.  FileReader.onloadend is async and can
      // fire AFTER that null propagates, silently dropping the last chunk.
      // Capturing it now guarantees we always have the right ID.
      const capturedSid = sessionIdRef.current;
      if (!capturedSid) return; // no active session — nothing to record

      const sendChunk = (blob: Blob) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const socket = socketRef.current;
          // Use captured sid, not the ref that may already be null
          if (!socket || !capturedSid) return;
          const b64 = (reader.result as string).split(',')[1];
          if (!b64) return;
          console.log(`[useWebRTC] Sending audio chunk: ${b64.length} chars, session=${capturedSid}`);
          socket.emit('audio_data_chunk', { session_id: capturedSid, chunk: b64, mime_type: mimeType });
          socket.emit('call:live:chunk', { session_id: capturedSid, chunk: b64, mime_type: mimeType });
        };
        reader.readAsDataURL(blob);
      };

      const recordChunk = () => {
        if (!active) return;
        let recorder: MediaRecorder;
        // ── FIX 2: Wrap MediaRecorder construction + start in try-catch ──────
        // If start() throws (e.g. track not yet live), the onstop chain breaks
        // and no further chunks are sent for the whole call.
        try {
          recorder = new MediaRecorder(audioStream, { mimeType });
        } catch {
          try { recorder = new MediaRecorder(audioStream); } catch { return; }
        }
        recorder.ondataavailable = (e: BlobEvent) => {
          if (e.data.size > 0) sendChunk(e.data);
        };
        recorder.onstop = () => { if (active) recordChunk(); };
        try {
          recorder.start();
        } catch (err) {
          console.warn('[useWebRTC] MediaRecorder.start() failed:', err);
          return; // don't schedule next chunk — try again on next remoteStream change
        }
        mediaRecorderRef.current = recorder;
        setTimeout(() => { if (recorder.state !== 'inactive') recorder.stop(); }, 5000);
      };

      recordChunk();
      console.log('[useWebRTC] Audio streaming started:', mimeType);

      return () => {
        active = false;
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
          mediaRecorderRef.current.stop();
        }
        mediaRecorderRef.current = null;
      };
    } catch (err) {
      console.error('[useWebRTC] MediaRecorder failed to start:', err);
    }

    return stopExisting;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callState, remoteStream]);

  // ── Call recording — upload helper ────────────────────────────────────────
  const uploadRecording = useCallback(async (blob: Blob, sid: string) => {
    if (!blob.size) return;
    const form = new FormData();
    form.append('recording', blob, `${sid}.webm`);
    form.append('session_id', sid);
    // Build upload URL using REACT_APP_API_PATH (e.g. "api2/v1") so it routes
    // through the same nginx proxy prefix as all other API calls.
    const apiPath = process.env.REACT_APP_API_PATH || 'api/v1';
    const uploadUrl = `${socketUrl}/${apiPath}/calls/upload-recording`;
    try {
      const res = await fetch(uploadUrl, {
        method: 'POST',
        body: form,
      });
      if (res.ok) {
        const data = await res.json();
        console.log('[useWebRTC] Recording uploaded:', data.recording_url);
      } else {
        console.warn('[useWebRTC] Recording upload returned', res.status);
      }
    } catch (err) {
      console.error('[useWebRTC] Recording upload failed:', err);
    }
  }, [socketUrl]);

  // ── Call recording — start/stop MediaRecorder ─────────────────────────────
  // Mixes local + remote audio via Web Audio API into a single MediaRecorder.
  // Starts automatically when the call is CONNECTED; stops and uploads on end.
  useEffect(() => {
    const isConnected = callState === CallState.CONNECTED;

    // Stop any existing recorder when we leave CONNECTED state
    const stopRecorder = () => {
      if (callRecorderRef.current && callRecorderRef.current.state !== 'inactive') {
        callRecorderRef.current.stop(); // triggers onstop → upload
      }
      callRecorderRef.current = null;
      // Guard: only close if not already closed (prevents "Cannot close a closed AudioContext")
      if (callAudioCtxRef.current && callAudioCtxRef.current.state !== 'closed') {
        callAudioCtxRef.current.close().catch(() => {});
      }
      callAudioCtxRef.current = null;
      setIsRecording(false);
    };

    if (!isConnected || !localStream || !remoteStream) {
      stopRecorder();
      return;
    }

    // Verify there are live audio tracks to record
    const localAudio = localStream.getAudioTracks().filter(t => t.readyState === 'live');
    const remoteAudio = remoteStream.getAudioTracks().filter(t => t.readyState === 'live');
    if (localAudio.length === 0 && remoteAudio.length === 0) return;

    try {
      // Mix both audio sources into one destination stream via AudioContext
      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
      const ctx: AudioContext = new AudioCtx();
      callAudioCtxRef.current = ctx;
      const dest = ctx.createMediaStreamDestination();

      if (localAudio.length > 0) {
        ctx.createMediaStreamSource(new MediaStream(localAudio)).connect(dest);
      }
      if (remoteAudio.length > 0) {
        ctx.createMediaStreamSource(new MediaStream(remoteAudio)).connect(dest);
      }

      // Combined stream: mixed audio + remote video tracks (video calls)
      const combined = new MediaStream([
        ...dest.stream.getAudioTracks(),
        ...remoteStream.getVideoTracks(),
      ]);

      const mimeType =
        ['video/webm;codecs=vp8,opus', 'video/webm', 'audio/webm;codecs=opus', 'audio/webm']
          .find(t => MediaRecorder.isTypeSupported(t)) ?? '';
      if (!mimeType) {
        console.warn('[useWebRTC] No supported recording MIME type — recording skipped');
        if (ctx.state !== 'closed') ctx.close().catch(() => {});
        callAudioCtxRef.current = null;
        return;
      }

      callRecordingChunksRef.current = [];
      recordingSessionRef.current = sessionIdRef.current;

      const recorder = new MediaRecorder(combined, { mimeType });
      recorder.ondataavailable = (e: BlobEvent) => {
        if (e.data.size > 0) callRecordingChunksRef.current.push(e.data);
      };
      recorder.onstop = async () => {
        // Null the ref first so no other code path tries to close it too
        callAudioCtxRef.current = null;
        // Guard: ctx may already be closed if stopRecorder ran before onstop fired
        if (ctx.state !== 'closed') {
          try { await ctx.close(); } catch (_) {}
        }
        const sid = recordingSessionRef.current;
        const chunks = callRecordingChunksRef.current;
        if (!sid || chunks.length === 0) return;
        const blob = new Blob(chunks, { type: mimeType });
        callRecordingChunksRef.current = [];
        await uploadRecording(blob, sid);
      };

      recorder.start(5000); // 5-second timeslices keep memory bounded
      callRecorderRef.current = recorder;
      setIsRecording(true);
      console.log('[useWebRTC] Call recording started:', mimeType);
    } catch (err) {
      console.error('[useWebRTC] Failed to start call recorder:', err);
      if (callAudioCtxRef.current && callAudioCtxRef.current.state !== 'closed') {
        callAudioCtxRef.current.close().catch(() => {});
      }
      callAudioCtxRef.current = null;
    }

    return stopRecorder;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callState, localStream, remoteStream, uploadRecording]);

  // Initialize peer connection
  const initPeerConnection = useCallback(() => {
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
    }

    const pc = new RTCPeerConnection(config.connectionOptions);

    // Handle ICE candidates
    pc.onicecandidate = (event) => {
      if (event.candidate && sessionIdRef.current) {
        socketRef.current?.emit('call:ice-candidate', {
          session_id: sessionIdRef.current,
          candidate: event.candidate
        });
      }
    };

    // Handle connection state changes
    pc.onconnectionstatechange = () => {
      const state = pc.connectionState as ConnectionState;
      handleConnectionStateChange(state);

      if (state === ConnectionState.FAILED || state === ConnectionState.CLOSED) {
        handleError(WebRTCError.CONNECTION_FAILED);
      }
    };

    // Handle remote stream
    pc.ontrack = (event) => {
      if (event.streams && event.streams[0]) {
        remoteStreamRef.current = event.streams[0];
        // Always create a new MediaStream object so React re-renders when a
        // second track (e.g. video after audio) is added to the same stream.
        // Without this, same-reference updates are bailed out by React and
        // isAudioOnly stays stale → video pane never shows.
        setRemoteStream(new MediaStream(event.streams[0].getTracks()));
        onRemoteStream?.(event.streams[0]);
      } else {
        // Fallback: some browsers don't populate event.streams — accumulate manually
        if (!remoteStreamRef.current) remoteStreamRef.current = new MediaStream();
        remoteStreamRef.current.addTrack(event.track);
        setRemoteStream(new MediaStream(remoteStreamRef.current.getTracks()));
        onRemoteStream?.(remoteStreamRef.current);
      }
    };

    // Handle negotiation needed (for re-negotiation)
    pc.onnegotiationneeded = async () => {
      try {
        if (callState === CallState.CONNECTED) {
          const offer = await pc.createOffer();
          await pc.setLocalDescription(offer);
          
          socketRef.current?.emit('call:offer', {
            session_id: sessionIdRef.current,
            offer: offer
          });
        }
      } catch (err) {
        console.error('Negotiation failed:', err);
        handleError(WebRTCError.CONNECTION_FAILED);
      }
    };

    peerConnectionRef.current = pc;
    return pc;
  }, [config.connectionOptions, callState, handleConnectionStateChange, onRemoteStream, handleError]);

  // Start local media stream
  const startLocalStream = useCallback(async () => {
    try {
      setError(null);
      const constraints = videoEnabledRef.current ? VIDEO_CONSTRAINTS : config.constraints;
      const stream = await getMediaStream(constraints);
      setLocalStream(stream);
      localStreamRef.current = stream;

      // Add tracks to peer connection (guard against a stale closed PC)
      const pc = peerConnectionRef.current;
      if (pc && pc.signalingState !== 'closed') {
        stream.getTracks().forEach((track: MediaStreamTrack) => {
          pc.addTrack(track, stream);
        });
      }
    } catch (err) {
      console.error('Failed to start local stream:', err);
      handleError(err as WebRTCError);
    }
  }, [config.constraints, handleError]);

  // Stop local media stream
  const stopLocalStream = useCallback(() => {
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => {
        track.stop();
      });
      setLocalStream(null);
      localStreamRef.current = null;
    }
  }, []);

  // Toggle video on/off during a call.
  // If a video track already exists, toggle its enabled flag.
  // If starting from audio-only, send a permission request to the remote peer
  // instead of grabbing the camera immediately. The actual renegotiation happens
  // inside doVideoUpgrade() after both sides consent.
  const doVideoUpgrade = useCallback(async () => {
    if (!localStreamRef.current) return;
    try {
      const videoStream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
      const videoTrack = videoStream.getVideoTracks()[0];
      localStreamRef.current.addTrack(videoTrack);
      setLocalStream(new MediaStream(localStreamRef.current.getTracks()));
      videoEnabledRef.current = true;
      setIsVideoEnabled(true);

      const pc = peerConnectionRef.current;
      if (pc && pc.signalingState !== 'closed') {
        pc.addTrack(videoTrack, localStreamRef.current);
        const offer = await pc.createOffer({ offerToReceiveAudio: true, offerToReceiveVideo: true });
        await pc.setLocalDescription(offer);
        socketRef.current?.emit('call:offer', { session_id: sessionIdRef.current, offer });
      }
    } catch (err) {
      console.error('[useWebRTC] Failed to get video for upgrade:', err);
    }
  }, []);

  const toggleVideo = useCallback(async () => {
    if (!localStreamRef.current) return;

    const existingVideoTracks = localStreamRef.current.getVideoTracks();
    const turningOn = !videoEnabledRef.current;

    if (existingVideoTracks.length > 0) {
      // Camera track already acquired — just enable/disable in-place
      existingVideoTracks.forEach(track => { track.enabled = turningOn; });
      videoEnabledRef.current = turningOn;
      setIsVideoEnabled(turningOn);
    } else if (turningOn) {
      // Audio-only → video upgrade: ask the remote peer for consent first.
      // The upgrade completes in the video_upgrade_accepted socket handler.
      socketRef.current?.emit('video_upgrade_request', { session_id: sessionIdRef.current });
    }
  }, []);

  /** Called when WE receive a video_upgrade_request and the user clicks Accept. */
  const acceptVideoUpgrade = useCallback(async () => {
    setVideoUpgradeRequested(false);
    socketRef.current?.emit('video_upgrade_accepted', { session_id: sessionIdRef.current });
    // Acquire our camera so tracks are in the PC before the renegotiation offer arrives
    await doVideoUpgrade();
  }, [doVideoUpgrade]);

  /** Called when WE receive a video_upgrade_request and the user clicks Decline. */
  const declineVideoUpgrade = useCallback(() => {
    setVideoUpgradeRequested(false);
    socketRef.current?.emit('video_upgrade_declined', { session_id: sessionIdRef.current });
  }, []);

  // Toggle mute
  const toggleMute = useCallback(() => {
      if (localStreamRef.current) {
      const audioTracks = localStreamRef.current.getAudioTracks();
      audioTracks.forEach((track: MediaStreamTrack) => {
        track.enabled = !track.enabled;
      });
      setIsMuted(!isMuted);
    }
  }, [isMuted]);

  // Create call offer
  const createCallOffer = useCallback(async () => {
    if (!peerConnectionRef.current || !localStreamRef.current) {
      throw new Error('Peer connection or local stream not initialized');
    }

    try {
      const offer = await peerConnectionRef.current.createOffer({
        offerToReceiveAudio: true,
        offerToReceiveVideo: videoEnabledRef.current
      });

      await peerConnectionRef.current.setLocalDescription(offer);

      return offer;
    } catch (err) {
      console.error('Failed to create offer:', err);
      throw err;
    }
  }, []);

  // Handle remote answer
  const handleRemoteAnswer = useCallback(async (answer: RTCSessionDescriptionInit) => {
    if (!peerConnectionRef.current) {
      throw new Error('Peer connection not initialized');
    }

    try {
      await peerConnectionRef.current.setRemoteDescription(answer);
      handleStateChange(CallState.CONNECTED);
    } catch (err) {
      console.error('Failed to set remote answer:', err);
      throw err;
    }
  }, [handleStateChange]);

  // Add ICE candidate
  const addIceCandidate = useCallback(async (candidate: RTCIceCandidateInit) => {
    if (!peerConnectionRef.current) {
      // Queue candidate if peer connection not ready
      pendingCandidatesRef.current.push(candidate);
      return;
    }

    try {
      await peerConnectionRef.current.addIceCandidate(candidate);
    } catch (err) {
      console.error('Failed to add ICE candidate:', err);
      // Don't throw error for ICE candidate failures as they're often non-critical
    }
  }, []);

  // Process pending ICE candidates
  const processPendingCandidates = useCallback(() => {
    if (peerConnectionRef.current && pendingCandidatesRef.current.length > 0) {
      pendingCandidatesRef.current.forEach(candidate => {
        addIceCandidate(candidate);
      });
      pendingCandidatesRef.current = [];
    }
  }, [addIceCandidate]);

  // Start a call
  const startCall = useCallback(async (targetAgentId?: string, metadata: any = {}) => {
    try {
      setError(null);
      handleStateChange(CallState.INITIATING);

      // Explicitly sync video mode from metadata — always set both ref and state
      // so that a hook constructed with enableVideo:true doesn't open the camera
      // for an audio-only outbound call.
      if (metadata.enableVideo) {
        videoEnabledRef.current = true;
        setIsVideoEnabled(true);
      } else {
        videoEnabledRef.current = false;
        setIsVideoEnabled(false);
      }

      // Generate session ID
      const newSessionId = generateSessionId();
      setSessionId(newSessionId);

      // Store metadata — include agent_id so the backend can record "who called who"
      metadataRef.current = {
        ...metadata,
        customer_id: metadata.customer_id || 'anonymous',
        call_type: metadata.enableVideo ? 'video' : 'audio',
        agent_id: agentIdRef.current,
        start_time: Date.now()
      };

      // Init peer connection FIRST so startLocalStream can addTrack to a fresh PC
      initPeerConnection();

      // Ensure we have a local stream (adds tracks to the peer connection above)
      if (!localStreamRef.current) {
        await startLocalStream();
      }

      // Send call initiation request
      socketRef.current?.emit('call:initiate', {
        customer_id: metadataRef.current.customer_id,
        conversation_id: metadata.conversation_id,
        metadata: metadataRef.current
      });

    } catch (err) {
      console.error('Failed to start call:', err);
      handleError(err as WebRTCError);
    }
  }, [startLocalStream, handleStateChange, initPeerConnection, handleError]);

  // Accept incoming call
  const acceptCall = useCallback(async (callSessionId: string, callType?: 'audio' | 'video') => {
    try {
      setError(null);
      setSessionId(callSessionId);
      handleStateChange(CallState.CONNECTING);

      // Explicitly set video mode based on call type — don't rely on the hook's
      // initial enableVideo option (which may be true even for audio-only calls).
      if (callType === 'video') {
        videoEnabledRef.current = true;
        setIsVideoEnabled(true);
      } else {
        videoEnabledRef.current = false;
        setIsVideoEnabled(false);
      }

      // Init peer connection FIRST so startLocalStream can addTrack to a fresh PC
      initPeerConnection();

      // Ensure we have a local stream (adds tracks to the peer connection above)
      if (!localStreamRef.current) {
        await startLocalStream();
      }

      // Send acceptance
      socketRef.current?.emit('call:accept', {
        session_id: callSessionId
      });

    } catch (err) {
      console.error('Failed to accept call:', err);
      handleError(err as WebRTCError);
    }
  }, [startLocalStream, handleStateChange, initPeerConnection, handleError]);

  // Decline incoming call
  const declineCall = useCallback(async (callSessionId: string) => {
    try {
      socketRef.current?.emit('call:decline', {
        session_id: callSessionId
      });

      handleStateChange(CallState.IDLE);
      setSessionId(null);
    } catch (err) {
      console.error('Failed to decline call:', err);
      handleError(err as WebRTCError);
    }
  }, [handleStateChange, handleError]);

  // End call
  const endCall = useCallback(async () => {
    try {
      // ── FIX 3: Flush the active audio recorder BEFORE emitting call:end ───
      // The recorder uses a 4-second stop/restart cycle.  If the call ends
      // mid-cycle the current recorder has unsent audio.  Stopping it here
      // triggers ondataavailable → sendChunk → socket.emit, all of which
      // happen *before* the call:end event reaches the backend, so the final
      // chunk makes it into the buffer before transcribe_and_store runs.
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
        // Give FileReader.onloadend one tick to complete and socket.emit to queue
        await new Promise<void>(resolve => setTimeout(resolve, 100));
      }

      if (sessionIdRef.current) {
        socketRef.current?.emit('call:end', {
          session_id: sessionIdRef.current
        });
      }

      // Clean up peer connection
      if (peerConnectionRef.current) {
        peerConnectionRef.current.close();
        peerConnectionRef.current = null;
      }

      // Guard AudioContext close — prevents "Cannot close a closed AudioContext"
      if (callAudioCtxRef.current && callAudioCtxRef.current.state !== 'closed') {
        callAudioCtxRef.current.close().catch(() => {});
      }
      callAudioCtxRef.current = null;

      // Stop all remote stream tracks explicitly before clearing the ref
      if (remoteStreamRef.current) {
        remoteStreamRef.current.getTracks().forEach(track => track.stop());
      }

      // Clean up streams
      stopLocalStream();
      setRemoteStream(null);
      remoteStreamRef.current = null;

      // Update metadata
      if (metadataRef.current.start_time) {
        metadataRef.current.end_time = Date.now();
        metadataRef.current.duration = metadataRef.current.end_time - metadataRef.current.start_time;
      }

      handleStateChange(CallState.ENDED);
      setSessionId(null);
      setIncomingCallData(null);
      setError(null);
      setLiveCaption('');
      liveCaptionAccRef.current = '';
      setMyCaption('');
      setRemoteCaption('');
      myCaptionAccRef.current = '';
      remoteCaptionAccRef.current = '';

    } catch (err) {
      console.error('Failed to end call:', err);
      handleError(err as WebRTCError);
    }
  }, [stopLocalStream, handleStateChange, handleError]);

  // Connect to socket
  const connect = useCallback(() => {
    if (socketRef.current?.connected) {
      return;
    }

    const socket = io(socketUrl, {
      path:
        process.env.REACT_APP_SOCKET_PATH ??
        `/${process.env.REACT_APP_API_PATH}/ws/socket.io`,
      transports: ["websocket", "polling"],
    });

    socket.on('connect', () => {
      console.log('Socket connected');
      if (isAgentRef.current) {
        socket.emit('agent:register', { agent_id: agentIdRef.current });
      }
      if (customerIdRef.current) {
        socket.emit('customer:register', { customer_id: customerIdRef.current });
      }
    });

    socket.on('disconnect', () => {
      console.log('Socket disconnected');
      handleConnectionStateChange(ConnectionState.DISCONNECTED);
    });

    // Call event handlers
    socket.on('call:initiate:response', (response: any) => {
      if (response.success) {
        // Replace the locally-generated session ID with the backend's canonical one.
        // Without this, call:offer is sent with the wrong session_id and the backend
        // can't route it — silently dropping the offer.
        if (response.session_id) {
          setSessionId(response.session_id);
        }
        handleStateChange(CallState.RINGING);
      } else {
        handleError(response.message);
      }
    });

    socket.on('call:incoming', (data: any) => {
      handleStateChange(CallState.RINGING);
      metadataRef.current.incoming_call = data;
      setIncomingCallData({
        session_id: data.session_id,
        caller_id: data.caller_id,
        call_type: data.call_type === 'video' ? 'video' : 'audio',
        conversation_id: data.conversation_id,
        timestamp: data.timestamp,
        handoff: data.handoff && typeof data.handoff === 'object' ? data.handoff : undefined,
      });
    });

    socket.on('call:accepted', async (_data: any) => {
      console.log('[AGENT CALL] call:accepted received — sessionId:', sessionIdRef.current, '| PC:', !!peerConnectionRef.current, '| localStream:', !!localStreamRef.current);
      handleStateChange(CallState.CONNECTING);
      processPendingCandidates();
      // Agent-initiated flow: after customer accepts, the agent (caller) creates and sends the offer.
      // In customer-initiated flow the agent never receives call:accepted — only the customer does.
      try {
        const offer = await createCallOffer();
        console.log('[AGENT CALL] ✅ Offer created, emitting call:offer for session:', sessionIdRef.current);
        socket.emit('call:offer', {
          session_id: sessionIdRef.current,
          offer
        });
      } catch (err) {
        console.error('[AGENT CALL] ❌ call:accepted — failed to create/send offer:', err, '| PC:', !!peerConnectionRef.current, '| stream:', !!localStreamRef.current, '| sessionId:', sessionIdRef.current);
      }
    });

    socket.on('call:declined', (data: any) => {
      handleStateChange(CallState.IDLE);
      setSessionId(null);
      setIncomingCallData(null);
    });

    socket.on('call:ended', (data: any) => {
      endCall();
    });

    socket.on('call:offer', async (data: SDPMessage) => {
      try {
        if (data.session_id !== sessionIdRef.current) return;

        const pc = peerConnectionRef.current;
        if (!pc) return;

        await pc.setRemoteDescription(data.offer as RTCSessionDescriptionInit);
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);

        socket.emit('call:answer', {
          session_id: sessionIdRef.current,
          answer: answer
        });

        handleStateChange(CallState.CONNECTED);
        processPendingCandidates();
      } catch (err) {
        console.error('Failed to handle offer:', err);
        handleError(WebRTCError.SIGNALING_ERROR);
      }
    });

    socket.on('call:answer', async (data: SDPMessage) => {
      try {
        if (data.session_id !== sessionIdRef.current) return;
        await handleRemoteAnswer(data.answer as RTCSessionDescriptionInit);
      } catch (err) {
        console.error('Failed to handle answer:', err);
        handleError(WebRTCError.SIGNALING_ERROR);
      }
    });

    socket.on('call:ice-candidate', async (data: ICECandidateMessage) => {
      try {
        if (data.session_id !== sessionIdRef.current) return;
        await addIceCandidate(data.candidate);
      } catch (err) {
        console.error('Failed to handle ICE candidate:', err);
      }
    });

    socket.on('call:error', (data: any) => {
      handleError(data.error);
    });

    // Live caption: backend Whisper transcribes remote-audio chunks and returns
    // text with speaker_sid so each side can label "You:" vs "Them:".
    const liveCaptionHandler = (data: { session_id: string; text: string; speaker_sid?: string }) => {
      if (data.session_id !== sessionIdRef.current) return;
      if (!data.text) return;

      // Maintain the flat combined caption for backwards-compat consumers
      liveCaptionAccRef.current = liveCaptionAccRef.current
        ? liveCaptionAccRef.current + ' ' + data.text
        : data.text;
      setLiveCaption(liveCaptionAccRef.current);

      // Split into "my" vs "remote" using speaker_sid vs our own socket id
      const isMine = data.speaker_sid && data.speaker_sid === socket.id;
      if (isMine) {
        myCaptionAccRef.current = myCaptionAccRef.current
          ? myCaptionAccRef.current + ' ' + data.text
          : data.text;
        setMyCaption(myCaptionAccRef.current);
      } else {
        remoteCaptionAccRef.current = remoteCaptionAccRef.current
          ? remoteCaptionAccRef.current + ' ' + data.text
          : data.text;
        setRemoteCaption(remoteCaptionAccRef.current);
      }
    };
    socket.on('call:live:caption', liveCaptionHandler);

    // ── Video upgrade permission flow ────────────────────────────────────────
    // Remote peer wants to enable video — surface a consent prompt in the UI.
    socket.on('video_upgrade_request', (data: { session_id: string }) => {
      if (data.session_id !== sessionIdRef.current) return;
      setVideoUpgradeRequested(true);
    });

    // Remote peer accepted our upgrade request — now grab our camera and renegotiate.
    socket.on('video_upgrade_accepted', async (data: { session_id: string }) => {
      if (data.session_id !== sessionIdRef.current) return;
      console.log('[useWebRTC] Video upgrade accepted — starting camera and renegotiating');
      await doVideoUpgrade();
    });

    // Remote peer declined our upgrade request — nothing to do except log.
    socket.on('video_upgrade_declined', (data: { session_id: string }) => {
      if (data.session_id !== sessionIdRef.current) return;
      console.log('[useWebRTC] Video upgrade declined by remote peer');
    });

    socketRef.current = socket;
  }, [
    socketUrl,
    handleStateChange,
    handleConnectionStateChange,
    handleRemoteAnswer,
    addIceCandidate,
    processPendingCandidates,
    createCallOffer,
    endCall,
    handleError,
    doVideoUpgrade
    // sessionId intentionally excluded: listeners read sessionIdRef.current instead,
    // preventing the socket from being torn down and recreated on every setSessionId call.
  ]);

  // Disconnect from socket
  const disconnect = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }
    handleConnectionStateChange(ConnectionState.CLOSED);
  }, [handleConnectionStateChange]);

  // Register as agent (includes agent_id so the backend can track who is calling)
  const registerAsAgent = useCallback(() => {
    isAgentRef.current = true;
    socketRef.current?.emit('agent:register', { agent_id: agentIdRef.current });
  }, []);

  // Unregister as agent
  const unregisterAsAgent = useCallback(() => {
    isAgentRef.current = false;
    socketRef.current?.emit('agent:unregister', {});
  }, []);

  // Register as customer so agents can call this user by their user ID
  const registerAsCustomer = useCallback((customerId: string) => {
    customerIdRef.current = customerId;
    socketRef.current?.emit('customer:register', { customer_id: customerId });
  }, []);

  // Initialize on mount
  useEffect(() => {
    if (autoConnect) {
      connect();
    }

    // Cleanup on unmount
    return () => {
      if (peerConnectionRef.current) {
        peerConnectionRef.current.close();
      }
      stopLocalStream();
      disconnect();
    };
  }, [autoConnect, connect, disconnect, stopLocalStream]);

  return {
    // State
    callState,
    connectionState,
    localStream,
    remoteStream,
    isMuted,
    sessionId,
    incomingCallData,
    error,
    liveCaption,
    myCaption,
    remoteCaption,
    videoUpgradeRequested,

    // Actions
    startCall,
    acceptCall,
    declineCall,
    endCall,
    toggleMute,
    toggleVideo,
    startLocalStream,
    stopLocalStream,
    isVideoEnabled,
    acceptVideoUpgrade,
    declineVideoUpgrade,
    isRecording,

    // Connection management
    connect,
    disconnect,
    socket: socketRef.current,

    // Agent management
    registerAsAgent,
    unregisterAsAgent,
    registerAsCustomer,
  };
}
